import { MAX_NESTING_LEVEL } from '../../xlsxExport.constants';

import frozenViewsBuilder from './frozenViewsBuilder';
import numberFormatBuilder from '../../utils/numberFormatBuilder';

import {
  PaddingColumnStyle,
  DataColumnStyle,
  TotalColumnStyle,
  PercentageColumnStyle,
} from '../../constants/styles';

const getStyle = (baseStyle, { percentage, hideDecimals, negativeMinus, shouldDisplayZero }) => ({
  ...baseStyle,
  numFmt: numberFormatBuilder(({ percentage, hideDecimals, negativeMinus, shouldDisplayZero })),
});

const buildColumnStyles = ({ maxNestingLevel, dataColumnCount, hideDecimals, negativeMinus, shouldDisplayZero }) => {
  const paddingStyles = new Array(maxNestingLevel).fill(PaddingColumnStyle);
  const dataColumnStyles = new Array(dataColumnCount).fill(
    getStyle(DataColumnStyle, { hideDecimals, negativeMinus, shouldDisplayZero })
  );
  const totalColumnStyle = getStyle(TotalColumnStyle, { hideDecimals, negativeMinus, shouldDisplayZero });

  return [
    ...paddingStyles,
    totalColumnStyle,
    getStyle(PercentageColumnStyle, { percentage: true, hideDecimals, negativeMinus, shouldDisplayZero }),
    ...dataColumnStyles,
  ];
};

const buildRowStylesAndMerges = ({
  grid,
  numberOfRowsToStick,
  maxNestingLevel,
  percentageRowsIndexes = [],
  nonCollapsedRowsIndexes = [],
  totalRowsIndexes = [],
  hideDecimals,
  negativeMinus,
  shouldDisplayZero,
}) => {
  const rowMerges = {};
  const rowStyles = grid.map((row, rowIndex) => {
    const rowStyle = {};

    if (rowIndex < numberOfRowsToStick) {
      return rowStyle;
    }

    if (totalRowsIndexes.includes(rowIndex)) {
      rowStyle.border = {
        top: { style: 'double' },
      };
    }

    const firstCellIndex = row.findIndex(cell => !!cell);

    if (firstCellIndex === 0 || firstCellIndex === 1) {
      rowStyle.outlineLevel = 0;
      rowStyle.font = { bold: true };
    } else {
      rowStyle.outlineLevel = firstCellIndex - 1;
      rowStyle.hidden = !nonCollapsedRowsIndexes.includes(rowIndex);
    }

    rowMerges[rowIndex] = {
      left: firstCellIndex + 1, // Excel.js is 1-indexed
      right: maxNestingLevel,
    };

    return rowStyle;
  });

  rowStyles[percentageRowsIndexes] = getStyle(rowStyles[percentageRowsIndexes], {
    percentage: true,
    hideDecimals,
    shouldDisplayZero,
    negativeMinus,
  });

  return { rowStyles, rowMerges };
};

const etrStyleBuilder = params => {
  const {
    sheetName,
    numberOfRowsToStick,
    numberOfColumnsToStick,
    maxNestingLevel = MAX_NESTING_LEVEL,
    dataColumnCount,
    percentageRowsIndexes,
    nonCollapsedRowsIndexes,
    totalRowsIndexes,
    grid,
    hideDecimals,
    negativeMinus,
    shouldDisplayZero,
  } = params;

  const { rowStyles, rowMerges } = buildRowStylesAndMerges({
    grid,
    numberOfRowsToStick,
    maxNestingLevel,
    percentageRowsIndexes,
    nonCollapsedRowsIndexes,
    totalRowsIndexes,
    hideDecimals,
    negativeMinus,
    shouldDisplayZero,
  });

  const metadata = {
    sheetName,
    views: frozenViewsBuilder({
      rows: numberOfRowsToStick,
      columns: numberOfColumnsToStick,
    }),
    columnStyles: buildColumnStyles({
      maxNestingLevel,
      dataColumnCount,
      hideDecimals,
      negativeMinus,
      shouldDisplayZero,
    }),
    rowStyles,
    rowMerges,
  };

  return metadata;
};

export default etrStyleBuilder;
